import React from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {useNavigation, useNavigationState, useRoute} from '@react-navigation/native';
import type {NativeStackNavigationProp} from '@react-navigation/native-stack';
import {useTranslation} from 'react-i18next';

import type {SettingsStackParamList} from '@/app/routes';
import {useFleetFeatures} from '@/entities/device/fleet';
import {useAuthStore} from '@/entities/session/auth-store';
import {fonts} from '@/shared/ui/fonts';
import {Icon, type IconName} from '@/shared/ui/icons';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Навигация раздела «Настройки» на широком окне — shadcn «settings layout» из
 * примера с формами: колонка ссылок слева, документ справа.
 *
 * ## Что это заменило
 *
 * На десктопе настройки были телефоном: список-хаб во всю ширину окна, а каждый
 * подэкран — одинокий документ с одной кнопкой «← Настройки» над ним. Чтобы попасть из
 * «Участников» в «Отделы», надо было подняться в хаб и спуститься обратно, — два
 * перехода ради соседней страницы. Здесь все страницы раздела стоят рядом, а
 * текущая подсвечена; переход — одно нажатие.
 *
 * Группы те же, что в хабе на телефоне, и по той же причине: у каждой один вопрос.
 * Что показывать, решает ВИД контекста, а не право (см. `SettingsScreen`).
 *
 * Активной считается не только сама страница, но и её потомки: в «Отделе» подсвечены
 * «Отделы», потому что читатель находится в них.
 */
type Screen = keyof SettingsStackParamList;

/** Страницы, на которые ведёт навигация, — все без параметров. */
type Target = Exclude<Screen, 'Department'>;

type Item = {
  screen: Target;
  labelKey: string;
  icon: IconName;
  /** Страницы под этой: в них она тоже подсвечена. */
  children?: Screen[];
};
type Group = {labelKey: string; items: Item[]};

const styles = StyleSheet.create({
  root: {gap: spacing(5), paddingTop: spacing(6)},
  group: {gap: 2},
  groupLabel: {paddingHorizontal: spacing(2), paddingBottom: spacing(1)},
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(2),
    height: 32,
    paddingHorizontal: spacing(2),
    borderRadius: radii.md,
  },
  label: {flex: 1, minWidth: 0, fontSize: 14, lineHeight: 20},
  labelOn: {fontFamily: fonts.sans.medium},
  labelOff: {fontFamily: fonts.sans.regular},
});

/** Курсор над строкой или палец на ней. */
function isHot(state: {pressed: boolean}): boolean {
  return state.pressed || (state as {hovered?: boolean}).hovered === true;
}

/**
 * Группа «Оборудование» (профили терминалов, интеграция и токены) спрятана из
 * «Настроек» по просьбе заказчика: настройки терминала правятся с карточки
 * устройства, а десктоп-мост и токены обычному владельцу не нужны. Экраны и
 * адреса (`/settings/terminals`, `/settings/integration`) остаются — вернуть
 * группу значит поменять одну константу, и рейка с хабом переключатся вместе.
 */
export const SHOW_ESTATE_GROUP = false;

/**
 * Стоит ли экран в стеке «Настроек». «Отделы» бывают и своей вкладкой (у компании из
 * одних камер, `OrgStack`), и там колонки настроек рядом с ними нет: её ссылки ведут
 * в экраны, которых в том стеке не существует.
 */
export function useInSettings(): boolean {
  return useNavigationState(s => s?.routeNames?.includes('Settings') ?? false);
}

export function useSettingsGroups(): Group[] {
  const kind = useAuthStore(s => s.activeContext?.kind) ?? 'home';
  // Без терминалов «Отделы» стоят своей вкладкой, а производственному календарю
  // считать нечего — явки нет (`entities/device/fleet`).
  const {attendance} = useFleetFeatures().features;
  const isCompany = kind === 'company';
  const hasEstate = SHOW_ESTATE_GROUP && (kind === 'company' || kind === 'home');

  const groups: Group[] = [];
  if (isCompany) {
    const company: Item[] = [
      {screen: 'Departments', labelKey: 'nav.departments', icon: 'building', children: ['Department']},
      {screen: 'Members', labelKey: 'members.title', icon: 'people'},
      {screen: 'CompanySettings', labelKey: 'settings.companyDetails', icon: 'settings'},
      {screen: 'CalendarSettings', labelKey: 'nav.calendar', icon: 'calendar'},
    ];
    groups.push({
      labelKey: 'settings.groupCompany',
      items: attendance
        ? company
        : company.filter(i => i.screen !== 'Departments' && i.screen !== 'CalendarSettings'),
    });
    groups.push({
      labelKey: 'settings.groupNotifications',
      items: [
        {screen: 'Telegram', labelKey: 'telegram.title', icon: 'send'},
        {screen: 'NotificationSettings', labelKey: 'notifSettings.title', icon: 'bell'},
      ],
    });
  }
  if (hasEstate) {
    groups.push({
      labelKey: 'settings.groupEstate',
      items: [
        {screen: 'TerminalProfiles', labelKey: 'more.terminalProfiles', icon: 'shield'},
        {screen: 'ApiBridge', labelKey: 'more.apiBridge', icon: 'desktop'},
      ],
    });
  }
  groups.push({
    labelKey: 'settings.groupAccount',
    items: [
      {screen: 'Account', labelKey: 'settings.account', icon: 'profile'},
      {screen: 'Security', labelKey: 'more.security', icon: 'lock'},
      {screen: 'PushSettings', labelKey: 'push.title', icon: 'bell'},
    ],
  });
  return groups;
}

export function SettingsNav() {
  const {t} = useTranslation();
  const p = useTheme();
  const route = useRoute();
  const navigation = useNavigation<NativeStackNavigationProp<SettingsStackParamList>>();
  const groups = useSettingsGroups();

  const current = route.name as Screen;

  const renderItem = (item: Item) => {
    const active = item.screen === current || (item.children ?? []).includes(current);
    return (
      <Pressable
        key={item.screen}
        testID={`settings-nav-${item.screen}`}
        accessibilityRole="link"
        accessibilityState={{selected: active}}
        accessibilityLabel={t(item.labelKey)}
        onPress={() => {
          if (item.screen !== current) {
            // `popTo`: обратно к странице раздела, а не новый экземпляр поверх.
            navigation.popTo(item.screen);
          }
        }}
        style={state => [
          styles.item,
          {backgroundColor: active ? p.muted : isHot(state) ? p.accent : 'transparent'},
        ]}>
        <Icon name={item.icon} size={16} color={active ? p.foreground : p.mutedForeground} />
        <AppText
          numberOfLines={1}
          color={active ? p.foreground : p.mutedForeground}
          style={[styles.label, active ? styles.labelOn : styles.labelOff]}>
          {t(item.labelKey)}
        </AppText>
      </Pressable>
    );
  };

  return (
    <View style={styles.root} testID="settings-nav">
      <View style={styles.group}>
        {renderItem({screen: 'Settings', labelKey: 'settings.overview', icon: 'profile'})}
      </View>
      {groups.map(group => (
        <View key={group.labelKey} style={styles.group}>
          <View style={styles.groupLabel}>
            <AppText variant="eyebrow">{t(group.labelKey)}</AppText>
          </View>
          {group.items.map(renderItem)}
        </View>
      ))}
    </View>
  );
}
